// Single source of truth for non-event-specific app constants.
// Event-specific values (hold minutes, max seats per reservation) live on the
// `events` row so admins can tune per event.

export const DEFAULT_HOLD_MINUTES = 5;
// Caps per reservation/phone: at most GROUND_SPECIAL_ZONE_MAX (3) seats from the
// "özel alan" (VIP) AND at most NON_ZONE_SEATS_MAX (4) seats from outside it.
// Both are enforced independently — there is NO "7 from the non-VIP area"
// allowance. Total tops out at 3 + 4 = 7.
export const DEFAULT_MAX_SEATS_PER_RESERVATION = 7;
export const NON_ZONE_SEATS_MAX = 4;

// Reference code: OKN-XXXX (base32, 4 chars after prefix) — short, readable, unguessable.
export const REFERENCE_CODE_PREFIX = 'OKN';
export const REFERENCE_CODE_LENGTH = 4;

// Crockford base32 (no I/L/O/U to avoid ambiguity).
export const REFERENCE_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export const VENUE_NAME = 'Okan Üniversitesi Bekir Okan Kültür Merkezi';

// When the public reservation system opens. Before this moment the booking page
// shows a countdown and the server rejects holds/reservations. Istanbul time.
export const RESERVATION_OPENS_AT = '2026-06-13T17:00:00+03:00';

/** True once the reservation system is open (now ≥ RESERVATION_OPENS_AT). */
export function reservationIsOpen(now: number = Date.now()): boolean {
  return now >= new Date(RESERVATION_OPENS_AT).getTime();
}

// Human-readable last date/time for the in-person approval meeting. The
// authoritative value is events.approval_deadline (2026-06-19T19:30+03:00);
// this short label is shown in the static marketing/booking chrome where we
// don't have the event row loaded.
export const APPROVAL_DEADLINE_TEXT = '19 Haziran 19.30';

export const TIMEZONE = 'Europe/Istanbul';

// Turkish clock convention writes time with a dot, not a colon. ICU's tr-TR
// still emits "19:30", so post-process formatted strings: "19:30" → "19.30".
// Only HH:MM groups are touched, so surrounding date text is left intact.
export function trDot(formatted: string): string {
  return formatted.replace(/(\d{1,2}):(\d{2})/g, '$1.$2');
}

// The single MVP event. Multi-event UI is out of scope (master prompt §2),
// but the data model supports more — adding events means inserting rows here.
export const MVP_EVENT_ID = '11111111-2222-4333-8444-555555555555';

// ─── Ground "özel alan" (taralı alan) ────────────────────────────────────────
// A customer may reserve AT MOST this many seats from the hatched ground zone.
// The zone covers rows B–K (every seat) plus row L seats 15–27. Membership is
// purely geometric (row label + seat number) so the FE (hatch + selection cap)
// and the API (server-side cap) stay in agreement without an extra DB column.
export const GROUND_SPECIAL_ZONE_MAX = 3;

const GROUND_SPECIAL_ZONE_FULL_ROWS = new Set(['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K']);

/** True when a GROUND seat falls inside the capped "özel alan". */
export function isGroundSpecialZoneSeat(rowLabel: string, seatNumber: number): boolean {
  if (GROUND_SPECIAL_ZONE_FULL_ROWS.has(rowLabel)) return true;
  if (rowLabel === 'L') return seatNumber >= 15 && seatNumber <= 27;
  return false;
}

// Canonical key for matching a phone across formats: the last 10 digits, which
// collapses "0555 111 2233", "+90 555 111 2233" and "5551112233" to the same
// value. Used for the per-phone seat caps (FE pre-check + server enforcement).
export function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, '').slice(-10);
}

// Fold a (Turkish) student name into a case- and diacritic-insensitive search
// key so prefix matching behaves identically on the FE and BE. Dotted/dotless
// i are unified, diacritics stripped, spaces collapsed: "İPEK İŞMAN" → "ipek
// isman", "ARYA NİL KAYA" → "arya nil kaya". Stored on students.search_name and
// applied to the incoming query before the LIKE 'prefix%' lookup.
const STUDENT_NAME_FOLD: Record<string, string> = {
  İ: 'i',
  I: 'i',
  ı: 'i',
  Ş: 's',
  ş: 's',
  Ğ: 'g',
  ğ: 'g',
  Ü: 'u',
  ü: 'u',
  Ö: 'o',
  ö: 'o',
  Ç: 'c',
  ç: 'c',
  Â: 'a',
  â: 'a',
  Î: 'i',
  î: 'i',
  Û: 'u',
  û: 'u',
};

export function normalizeStudentSearch(input: string): string {
  return input
    .replace(/[İIıŞşĞğÜüÖöÇçÂâÎîÛû]/g, (ch) => STUDENT_NAME_FOLD[ch] ?? ch)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// The 10-digit national part of a Turkish number, dropping any +90 / 90 / 0
// prefix. "+90 555…", "0555…", "90555…", "555…" all yield "555…" (max 10).
export function trPhoneDigits(input: string): string {
  let d = input.replace(/\D/g, '');
  if (d.startsWith('90') && d.length > 10) d = d.slice(2);
  else if (d.startsWith('0')) d = d.slice(1);
  return d.slice(0, 10);
}

/** A valid Turkish mobile number is 10 digits starting with 5 (5XXXXXXXXX). */
export function isValidTrMobile(input: string): boolean {
  return /^5\d{9}$/.test(trPhoneDigits(input));
}

/** Progressive display formatting → "0555 123 45 67". */
export function formatTrPhone(input: string): string {
  const d = trPhoneDigits(input);
  if (!d) return '';
  const parts = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 8), d.slice(8, 10)].filter(Boolean);
  return `0${parts.join(' ')}`;
}
